import Note from "../../models/soundslice/Note";
import accurateTimingWorker from "../accurateTimingWorker";
import {getAudioContext} from "../audioContext";
import {ISchedulePlayNote} from "../synthesizer/ISchedulePlayNote";
import {subscribeToSettingsEvents} from "../eventBus/settings/settingsEventBus";
import {SettingsEventTypes} from "../eventBus/settings/SettingsEventTypes";
import nativeBridge from "../nativeBridge";
import deviceInfo from "../deviceInfo/deviceInfo";
//simple type for clear timeout
type Func = () => void;

/**
 * Scheduling all notes in a song/segment with web audio .start(startTime) is not performant, and can lead to glitches, stutters, etc.
 * This class provides functionality for scheduling batches of notes.
 * e.g. group 5 bars/measures of notes, schedule them with web audio.  When that group gets close to finishing, schedule the next batch.
 */
export default class NotesPlaybackScheduler{
  //used to schedulePlayNote and stopAllScheduledPlays
  synthesizer: ISchedulePlayNote;
  //we batch scheduling of notes (for perf reasons). We need a ref to the clear timeout of the last setTimeout called (only 1 is called at a time), so we can stop playback when the user clicks stop.
  clearAccurateTimingWorkerSetTimeout: Func = ()=>{};

  constructor() {
    subscribeToSettingsEvents((message: SettingsEventTypes)=>{
      switch (message.type) {
        case "audioContextSampleRateChanged":
          this.cancelAllScheduledNotesPlayback();//shouldn't really be possible for this to happen during playback, but just in case.
          break;
      }
    });
  }
  /**
   * Start playing notes in a performant and accurate manner.
   * With large amount of notes, scheduling all web audio playbacks at once can lead to perf issues (stuttered playback, high cpu usage, etc).
   * To mitigate perf issues, we break the scheduling into batches into groups of bars/measures.  The next batch is scheduled toward the end of a given batch.
   * @param notes - typically all the notes for a given segment/song
   * @param speed - playback speed. 1 == 100%, .9 == 90%, etc.
   * @param synthesizer - used to schedulePlayNote, for a note at a given time
   * @param seekOffsetSeconds - used to modify a note's offset based on where the seek position is.
   *          e.g. if a note has an offset of 1, and the seekOffsetSeconds is 1, when play is clicked, the note should be scheduled as if it's offset was 0.
   */
  scheduleNotesPlayback(
    {
      notes,
      speed,
      synthesizer,
      seekOffsetSeconds,
      loopEndOffsetSeconds,
      getAudioCurrentTime
    } : {
      notes: Note[];
      speed: number;
      synthesizer: ISchedulePlayNote;
      seekOffsetSeconds: number;
      loopEndOffsetSeconds: number,
      getAudioCurrentTime?: () => number
    }){
    this.cancelAllScheduledNotesPlayback();
    this.synthesizer = synthesizer;
    const notesToSchedule = getNotesBetweenOffsets(notes, seekOffsetSeconds, loopEndOffsetSeconds);
    scheduleNotes(
      notesToSchedule,
      speed,
      seekOffsetSeconds,
      this.synthesizer,
      getAudioContext().currentTime,
      1,
      (clear: Func) => {
        this.clearAccurateTimingWorkerSetTimeout = clear;
      },
      performance.now() / 1000,
      getAudioCurrentTime
    );
  }

  /**
   * Stop playing all notes.  Clear any timeouts from scheduling.
   */
  cancelAllScheduledNotesPlayback(){
    if(this.synthesizer){
      this.synthesizer.stopAllScheduledPlays();
    }
    this.clearAccurateTimingWorkerSetTimeout();
  }
}

/**
 * Recursive function that batches notes into N bars, and schedules subsequent batches towards the end of the current batch.
 * @param notes - array of notes to be batched and played
 * @param speed - playback speed. e.g. 1 for 100%, .9 for 90%, etc
 * @param seekOffsetSeconds - seek position at start time
 * @param synthesizer - used to call scheduleNotePlay
 * @param originalAudioContextStartTimeSeconds - audioContext.currentTime at the time play was called.  used to determine the note's relative offset.
 * @param barsToBatch - number of bars/measures to include in a batch of scheduled notes.
 * @param setClearTimeout - function which is called whenever a new setTimeout is called.  This allows the parent caller to have reference to the func that stops a setTimeout from executing.
 * @param getAudioCurrentTime
 * @param referenceStartSeconds - used in place of originalAudioContextStartTimeSeconds for Android, as the audioContext.currentTime is not accurate.
 */
function scheduleNotes(
  notes: Note[],
  speed: number,
  seekOffsetSeconds: number,
  synthesizer: ISchedulePlayNote,
  originalAudioContextStartTimeSeconds: number,
  barsToBatch: number,
  setClearTimeout: (func: Func) => void, 
  referenceStartSeconds: number,
  getAudioCurrentTime?: () => number){
  if(notes.length <= 0) { return; }
  //break up notes into batches based on bars.
  const {noteBatch, remainingNotes} = getNextBatchOfNotes(notes, barsToBatch);
  //schedule notes that should be played
  noteBatch.map( n => scheduleNote(n, speed, synthesizer, originalAudioContextStartTimeSeconds, seekOffsetSeconds, referenceStartSeconds, getAudioCurrentTime));
  //stop recursion if there are no remaining notes.
  if(remainingNotes.length <= 0){ return; }
  //determine when a bar is going to end.
  const nextTimeMs = determineWhenNextBatchOfNotesShouldBeScheduledInMs(noteBatch, speed, originalAudioContextStartTimeSeconds, seekOffsetSeconds, referenceStartSeconds);
  //recursively call scheduleNotes, passing the remaining notes.
  const clearTimeout = accurateTimingWorker.setTimeout(() => { scheduleNotes(remainingNotes, speed, seekOffsetSeconds, synthesizer, originalAudioContextStartTimeSeconds, barsToBatch, setClearTimeout, referenceStartSeconds, getAudioCurrentTime) }, nextTimeMs);
  //give reference to the last scheduled clear timeout func, so we can clear timeout/playback when stop is pressed.
  setClearTimeout(clearTimeout);
}

/**
 * Returns ms value for when the next setTimeout should be scheduled to run.
 * Evaluates the offset/start time of the last note in the current batch, factors in the speed, as well as when the note scheduling began.
 * @param currentBatch - noteBatch result from getNextBatchOfNotes.  Last note of this array will have it's offset evaluated.
 * @param speed - playback speed. e.g. 1 for 100%, .9 for 90%, etc
 * @param originalAudioContextStartTimeSeconds - audioContext.currentTime at the time play was called.  used to determine the note's relative offset.
 * @param seekOffsetSeconds - seek position at start time
 * @param nextTimeBufferMs - once we determine how soon in ms the last note of the batch will begin playing, we subtract this value to ensure any latency in setTimeout, etc won't affect playback.
 */
function determineWhenNextBatchOfNotesShouldBeScheduledInMs(currentBatch: Note[], speed: number, originalAudioContextStartTimeSeconds: number, seekOffsetSeconds: number, referenceStartSeconds: number, nextTimeBufferMs = 50){
  //technically we can have several measures/bars without any note.  when that occurs, we will immediately schedule the next batch.
  if(currentBatch.length <= 0) { return 0; }

  //get the last note and determine how soon, in ms, it will begin.
  // const lastNote = currentBatch[currentBatch.length - 1];
  const highestOffsetInBatch = getHighestOffset(currentBatch);

  const speedAdjustedHighestOffset = highestOffsetInBatch / speed;
  const secondsSinceSongWasStarted = performance.now() / 1000 - referenceStartSeconds;
  const speedAdjustedSeekOffsetSeconds = seekOffsetSeconds / speed;
  let nextTimeMs = (speedAdjustedHighestOffset - secondsSinceSongWasStarted - speedAdjustedSeekOffsetSeconds) * 1000;

  //give ourselves a bit of extra time, in case setTimeout is lagging/off
  nextTimeMs = nextTimeMs > nextTimeBufferMs ? nextTimeMs - nextTimeBufferMs : 0;
  return nextTimeMs;
}

function getHighestOffset(notes: Note[]){
  let highestOffset = 0;
  if(notes.length <= 0){ return highestOffset; }
  for(let i = 0; i < notes.length; ++i){
    const note = notes[i];
    if(note.offset > highestOffset){
      highestOffset = note.offset;
    }
  }
  return highestOffset;
}
/**
 * Breaks an array of notes into 2 arrays:
 * 1 - array of notes that belong to the next N bars
 * 2 - array of remaining notes that don't belong to the next N bars.
 * @param notes - array of notes to be evaluated.
 * @param barsToBatch - number of bars that noteBatch should include notes from.
 */
function getNextBatchOfNotes(notes: Note[], barsToBatch=1){
  if(notes.length <= 0){ return {noteBatch: [], remainingNotes: []}; }
  const noteBatch = [];
  const remainingNotes = [];
  //figure out where we are in the song by looking at the first note's bar index.
  const startBar = notes[0].path.barIndex;
  const highestBar = startBar + barsToBatch;
  for(let i = 0; i < notes.length; ++i){
    const note = notes[i];
    if(note.path.barIndex < highestBar){
      noteBatch.push(note);
    }else{
      remainingNotes.push(note);
    }
  }
  return {noteBatch, remainingNotes};
}

/**
 * Schedule a note to be played using WebAudio source node start(startTime).
 * @param note - note which should be played.
 * @param speed - Note offsets (start) and durations are not changed when playback speed is changed, so we need to know the current playback speed in order to determine when the note should be started & stopped. e.g. 1, 1.1, .9, etc
 * @param synthesizer - used to schedulePlayNote
 * @param originalStartTimeSeconds - audioContext.currentTime when play was pressed.  Used to determine the relative start time of the note, based on its offset.
 * @param seekOffsetSeconds - a note's offset is always fixed, but when we use seek we need to change it so that the notes playback start as if the song started at the seek position.
 * @param getAudioCurrentTime - function for getting the current time from the html audio.  notesPlayer modifies the current time so that it's consistent between 2 backing tracks. i.e 2.4 at normal is 2.4 at slow.
 */
async function scheduleNote(
  note: Note,
  speed: number,
  synthesizer: ISchedulePlayNote,
  originalStartTimeSeconds: number,
  seekOffsetSeconds: number,
  referenceStartSeconds: number,
  getAudioCurrentTime?: () => number){
  if(note.tieEnd){ return; } // don't play the end of tie's as a second note.

  const { speedAdjustedDurationSeconds: noteSpeedAdjustedDurationSeconds, speedAdjustedOffsetSeconds: noteSpeedAdjustedOffsetSeconds} = getSpeedAdjustedOffsetAndDurationInSeconds(note, speed);

  //sync up with the audio.currentTime since getAudioContext.currentTime can get out of whack.
  //this is also useful when using html audio, as the await play() doesn't work well on safari, so we have to poll currentTime until it moves, then resolve AudioPlayer.play's promise, which note scheduling happens right after.
  let deltaBetweenAudioAndAudioContext = 0;
  const audioContextCurrentTime = getAudioContext().currentTime;
  if(getAudioCurrentTime !== undefined){
    const audioContextDurationSeconds = ((audioContextCurrentTime - originalStartTimeSeconds));
    const audioCurrentTime = (getAudioCurrentTime());
    const speedAdjustedAudioContextDurationSeconds = (audioContextDurationSeconds * speed);
    const audioContextDurationSecondsWithOffset = speedAdjustedAudioContextDurationSeconds + seekOffsetSeconds;
    deltaBetweenAudioAndAudioContext = audioCurrentTime - audioContextDurationSecondsWithOffset;
  }

  const startAtReferenceRelativeSeconds = referenceStartSeconds - deltaBetweenAudioAndAudioContext + noteSpeedAdjustedOffsetSeconds - (seekOffsetSeconds / speed);
  const startAtContextRelativeSeconds = originalStartTimeSeconds + noteSpeedAdjustedOffsetSeconds - (seekOffsetSeconds / speed);
  const now = (new Date()).getTime();
  const absoluteStartTimestamp = now + startAtReferenceRelativeSeconds * 1000 - performance.now();

  const {midi} = note;
  const velocity = !!note.velocity ? note.velocity : 100;
  const velocityForWebView = !deviceInfo.getIsAndroid() ? velocity : 0;

  // This class probably shouldn't know about count-ins vs notes, but it's the easiest way to get the count-in to work.
  // We can change this after we confirm that metronome & notes are synced up.
  const isCountIn = midi >= 1 && midi <= 6;
  if (isCountIn) {
    const word = countInWordFromMidiNumber(midi);
    if (word) {
      nativeBridge.sendMessageToNative({type: 'audio.countInWord_scheduled', word, playAtTimestamp: absoluteStartTimestamp, timestamp: Date.now() });
    }
  }
  synthesizer.schedulePlayNote({midi, velocity: velocityForWebView, startAtContextRelativeSeconds, durationInSeconds: noteSpeedAdjustedDurationSeconds});
}

function countInWordFromMidiNumber(midi: number) {
  switch (midi) {
    case 1: return "one";
    case 2: return "two";
    case 3: return "three";
    case 4: return "four";
    case 5: return "go";
    case 6: return "set";
    default: return "";
  }
}

/**
 * Note offsets (start) and durations are not changed when playback speed is changed, so we need to calculate new times based on the speed.
 * @param note - note with offset (when note should start) and duration.
 * @param speed - e.g. 1 for 100%, 1.1 for 110%, .9 for 90%, etc
 */
function getSpeedAdjustedOffsetAndDurationInSeconds(note: Note, speed: number){
  const {offset: offsetSeconds, duration, durationWithTie} = note;
  //notes that belong to a tie should be played as if they were one note.  Since duration doesn't represent the combined notes, use the durationWithTie prop, which we calculate ourselves, as the duration which the note should be played.
  const durationSeconds = durationWithTie !== undefined ? durationWithTie : duration;
  const speedAdjustedOffsetSeconds = offsetSeconds / speed;
  const speedAdjustedDurationSeconds = durationSeconds / speed;
  return {speedAdjustedOffsetSeconds, speedAdjustedDurationSeconds};
}

/**
 * Find and return notes after the specified seekOffsetSeconds and before loopEndOffsetSeconds
 * @param notes - array of notes to be evaluated
 * @param seekOffsetSeconds - where the cursor seek position is, in seconds.
 * @param loopEndOffsetSeconds - note offsets should come before this value
 */
function getNotesBetweenOffsets(notes: Note[], seekOffsetSeconds: number, loopEndOffsetSeconds: number){
  loopEndOffsetSeconds = loopEndOffsetSeconds < 0 ? Number.POSITIVE_INFINITY : loopEndOffsetSeconds;
  return notes.filter(n => n.offset >= seekOffsetSeconds && n.offset < loopEndOffsetSeconds);
}